import React, { useContext, useMemo, useEffect } from 'react'

import { PageHeader } from 'components/PageHeader'
import { UserContext } from 'contexts'
import { useApi } from 'hooks'
import { getUserRole, Role } from 'utils/roles'
import { ConfigUtils } from 'utils/config'

import { checkAnalystHasAllStores } from '../Stores/api'
import OverviewTab from './OverviewTab/OverviewTab'
import ClientScanTab from './ClientScanTab/ClientScanTab'
import LoyaltyEfficiencyTab from './LoyaltyEfficiencyTab/LoyaltyEfficiencyTab'
import MarketingTab from './MarketingTab/MarketingTab'
import TicketsByAppTab from './TicketsByAppTab/TicketsByAppTab'
import DownloadsTab from './DownloadsTab/DownloadsTab'
// import BusinessImpactTab from './BusinessImpactTab/BusinessImpactTab'
import { useTabManager } from './hooks/useTabManager'
import { DashboardFiltersProvider, useDashboardFilters } from './contexts/DashboardFiltersContext'

// Marcas que deben ocultar Marketing y Loyalty Efficiency
const shouldHideMarketingAndLoyaltyTabs = ConfigUtils.isApp(
  'cm', // Club
  'le-pain-quotidien', // Le Pain
  'ts', // Tante Sara
  'ronnie', // Ronnie
  'paru',
  'cs',
  'jb',
  'vicolo',
  'acai-brasil',
  'la-condesa',
  'el-diez',
)

const ALL_TABS = [
  { key: 'overview', label: 'Overview', component: OverviewTab },
  { key: 'client-scan', label: 'Client scan', component: ClientScanTab },
  { key: 'tickets-by-app', label: 'Tickets por App', component: TicketsByAppTab },
  { key: 'loyalty-efficiency', label: 'Loyalty efficiency', component: LoyaltyEfficiencyTab },
  { key: 'marketing', label: 'Marketing', component: MarketingTab },
  // { key: 'business-impact', label: 'Business impact', component: BusinessImpactTab },
  { key: 'downloads', label: 'Descargas', component: DownloadsTab },
]

// Filtrar tabs según la marca: Marketing y Loyalty Efficiency solo visibles en GE y Tea.
// Preview por URL: /new-dashboard?unlock=marketing (Club y Le Pain)
const TABS = ALL_TABS.filter(tab => {
  if (shouldHideMarketingAndLoyaltyTabs) {
    const unlocked =
      ConfigUtils.isApp('cm', 'le-pain-quotidien') &&
      new URLSearchParams(window.location.search).get('unlock') === 'marketing'
    if (!unlocked && (tab.key === 'marketing' || tab.key === 'loyalty-efficiency')) {
      return false
    }
  }
  return true
})

interface AnalystHasAllStoresResponse {
  hasAllStores: number
}

const DashboardContent = () => {
  const { admin } = useContext(UserContext)
  const { activeTab, handleTabChange, tabComponents } = useTabManager(TABS)
  const { setActiveTab } = useDashboardFilters()

  const userRole = getUserRole(admin)
  const isAnalyst = userRole === Role.Analyst

  const getAnalystHasAllStoresResponse = (responseData: AnalystHasAllStoresResponse) =>
    responseData?.hasAllStores ?? 0
  const [{ data: hasAllStores }] = useApi<AnalystHasAllStoresResponse, number>(
    checkAnalystHasAllStores,
    getAnalystHasAllStoresResponse,
    {
      baseData: 0,
    },
  )

  const visibleTabs = useMemo(() => {
    let filteredTabs = TABS
    if (isAnalyst && hasAllStores === 0) {
      filteredTabs = TABS.filter(tab => tab.key === 'overview')
    }
    return filteredTabs
  }, [isAnalyst, hasAllStores])

  const visibleTabComponents = useMemo(() => {
    const visibleTabKeys = new Set(visibleTabs.map(t => t.key))
    return tabComponents.filter(tab => visibleTabKeys.has(tab.key))
  }, [tabComponents, visibleTabs])

  useEffect(() => {
    const visibleTabKeys = visibleTabs.map(t => t.key)
    // Si el tab activo no está visible, cambiar al primer tab disponible
    if (!visibleTabKeys.includes(activeTab)) {
      const firstVisibleTab = visibleTabs[0]?.key || 'overview'
      handleTabChange(firstVisibleTab)
      setActiveTab(firstVisibleTab)
    }
  }, [visibleTabs, activeTab, handleTabChange, setActiveTab])

  const handleTabChangeWithContext = (tabKey: string) => {
    const visibleTabKeys = visibleTabs.map(t => t.key)
    // No permitir cambiar a un tab que no está visible
    if (!visibleTabKeys.includes(tabKey)) {
      return
    }
    if (isAnalyst && hasAllStores === 0 && tabKey !== 'overview') {
      return
    }
    handleTabChange(tabKey)
    setActiveTab(tabKey)
  }

  return (
    <div>
      <PageHeader
        title="Dashboard"
        tabs={visibleTabs.map(t => ({ key: t.key, label: t.label }))}
        activeTab={activeTab}
        onTabChange={handleTabChangeWithContext}
      />

      {visibleTabComponents.map(tab => {
        const Component = tab.component
        return (
          <div key={tab.key} style={{ display: tab.isActive ? 'block' : 'none' }}>
            {tab.isLoaded && <Component />}
          </div>
        )
      })}
    </div>
  )
}

const NewDashboard = () => {
  return (
    <DashboardFiltersProvider>
      <DashboardContent />
    </DashboardFiltersProvider>
  )
}

export default NewDashboard
